import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronUp,
  Plus,
  Eye,
  Trash2,
  Loader2,
  X,
  Play,
  Layers,
  Grid3X3,
  List,
  Edit,
  MessageSquare,
} from "lucide-react";

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { TriggerModal } from "../components/TriggerModal";
import { PlaybookFilters } from "../components/PlaybookFilters";
import { useToast } from "../components/ui/Toast";
import { v4 as uuidv4 } from "uuid";
import { Playbook } from "../types";
import { usePlaybooksApi } from "../apis/playbooks-apis";
import { usePlaybookFilters } from "../hooks/usePlaybookFilters";

const ITEMS_PER_PAGE = 20;

export const PlaybooksList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const playbooksApi = usePlaybooksApi();
  const containerRef = useRef<HTMLDivElement>(null);

  // New filtering system
  const playbookFilters = usePlaybookFilters("one-shot", false);

  // UI state
  const [displayedPlaybooks, setDisplayedPlaybooks] = useState<Playbook[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Modal states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playbookToDelete, setPlaybookToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook | null>(
    null
  );

  const [sessionId, setSessionId] = useState<string>(uuidv4());

  // Update displayed playbooks when filter results change
  useEffect(() => {
    setDisplayedPlaybooks(
      playbookFilters.filteredPlaybooks.slice(0, ITEMS_PER_PAGE)
    );
    setHasMore(playbookFilters.filteredPlaybooks.length > ITEMS_PER_PAGE);
  }, [playbookFilters.filteredPlaybooks]);

  // Show error toast if filtering fails
  useEffect(() => {
    if (playbookFilters.error) {
      addToast({
        title: "Error",
        description: playbookFilters.error,
        variant: "destructive",
      });
    }
  }, [playbookFilters.error, addToast]);

  const loadMorePlaybooks = useCallback(() => {
    if (playbookFilters.hasActiveFilters || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const startIndex = displayedPlaybooks.length;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    if (startIndex >= playbookFilters.filteredPlaybooks.length) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const newItems = playbookFilters.filteredPlaybooks.slice(
      startIndex,
      endIndex
    );
    setDisplayedPlaybooks((prev) => [...prev, ...newItems]);

    if (endIndex >= playbookFilters.filteredPlaybooks.length) {
      setHasMore(false);
    }

    setLoadingMore(false);
  }, [
    playbookFilters.hasActiveFilters,
    playbookFilters.filteredPlaybooks,
    loadingMore,
    hasMore,
    displayedPlaybooks.length,
  ]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        hasMore &&
        !loadingMore &&
        !playbookFilters.hasActiveFilters
      ) {
        loadMorePlaybooks();
      }
    }, options);

    const loader = document.querySelector("#scroll-loader");
    if (loader) {
      observer.observe(loader);
    }

    return () => observer.disconnect();
  }, [
    loadMorePlaybooks,
    hasMore,
    loadingMore,
    playbookFilters.hasActiveFilters,
  ]);

  const handleViewPlaybook = (playbookId: string) => {
    navigate(`/studio/playbooks/view/${playbookId}`);
  };

  const handleEditPlaybook = (playbookId: string) => {
    navigate(`/studio/playbooks/edit/${playbookId}`);
  };

  const handleTriggerClick = (playbook: Playbook) => {
    setSelectedPlaybook(playbook);
    setTriggerModalOpen(true);
  };

  const handleTriggerSubmit = async (playbookId: string, inputData: string) => {
    await playbooksApi.triggerPlaybook(playbookId, inputData);
  };

  const handleDeleteClick = (id: string) => {
    setPlaybookToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!playbookToDelete) return;

    setIsDeletingId(playbookToDelete);
    try {
      await playbooksApi.deletePlaybook(playbookToDelete);
      addToast({
        title: "Success",
        description: "Playbook successfully deleted",
        variant: "default",
      });

      // Refresh playbooks using the new filtering system
      await playbookFilters.refreshPlaybooks();
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to delete playbook. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setPlaybookToDelete(null);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setShowScrollTop(window.scrollY > 400);
      }, 200);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  // Use filtered playbooks for display
  const currentPlaybooks = playbookFilters.hasActiveFilters
    ? playbookFilters.filteredPlaybooks
    : displayedPlaybooks;
  const currentTotal = playbookFilters.filteredPlaybooks.length;

  const renderPlaybookCard = (pkg: Playbook, sessionId: string) => (
    <Card
      key={pkg.playbookId}
      className="group hover:shadow-2xl transition-all duration-300 border-sky-200 hover:border-sky-400 hover:scale-[1.02] relative ring-2 ring-transparent hover:ring-sky-300/40 bg-gradient-to-br from-white to-sky-50/30"
    >
      <CardContent className="p-4">
        <div className="flex flex-col">
          {/* Top section - Icon and Info */}
          <div className="flex items-start gap-4 mb-4">
            {/* Playbook Icon */}
            <div className="bg-gradient-to-br from-sky-100 to-cyan-200 p-3 rounded-xl flex-shrink-0 shadow-sm border border-sky-100">
              <Layers className="h-6 w-6 text-sky-600" />
            </div>

            {/* Playbook Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-sky-600 transition-colors truncate tracking-tight">
                  {pkg.playbookName}
                </h3>
                {pkg.currentVersion && (
                  <Badge className="bg-sky-100 text-sky-700 text-xs flex-shrink-0">
                    v{pkg.currentVersion}
                  </Badge>
                )}
                {pkg.playbookProductName && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-sky-400 to-cyan-500 text-white shadow-sm flex-shrink-0">
                    {pkg.playbookProductName}
                  </span>
                )}
              </div>

              <p className="text-sm text-gray-600 min-h-[40px]">
                {pkg.playbookDescription && pkg.playbookDescription.length > 300
                  ? `${pkg.playbookDescription.substring(0, 300)}...`
                  : pkg.playbookDescription || "No description provided."}
              </p>
            </div>
          </div>

          {/* Tags section - before buttons */}
          {pkg.playbookTags && pkg.playbookTags.length > 0 && (
            <div className="mt-3 pt-2 border-t border-gray-100">
              <div className="flex flex-wrap gap-1">
                {pkg.playbookTags.slice(0, 4).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
                {pkg.playbookTags.length > 4 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    +{pkg.playbookTags.length - 4} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Bottom section - Actions */}
          <div className="flex items-center justify-center gap-3 pt-3 border-t border-gray-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewPlaybook(pkg.playbookId)}
              className="border-2 border-sky-300 text-sky-600 hover:bg-sky-50 hover:border-sky-400 transition-all duration-200 px-4 py-2 font-semibold"
              title="View Playbook"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Eye className="h-4 w-4 mr-2" />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditPlaybook(pkg.playbookId)}
              className="border-2 border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 px-4 py-2 font-semibold"
              title="Edit Playbook"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTriggerClick(pkg)}
              className="border-2 border-green-300 text-green-600 hover:bg-green-50 hover:border-green-400 transition-all duration-200 px-4 py-2 font-semibold"
              title="Trigger Playbook"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Play className="h-4 w-4 mr-2" />
              Trigger
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteClick(pkg.playbookId)}
              className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all duration-200 px-4 py-2 font-semibold"
              disabled={isDeletingId === pkg.playbookId}
              title="Delete Playbook"
            >
              {isDeletingId === pkg.playbookId ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderPlaybookListItem = (pkg: Playbook) => (
    <Card
      key={pkg.playbookId}
      className="mb-3 bg-gradient-to-r from-white to-sky-50/30 border border-sky-200 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.01] hover:border-sky-300 group"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Playbook Icon */}
            <div className="bg-gradient-to-br from-sky-100 to-cyan-200 p-2 rounded-lg flex-shrink-0 shadow-sm">
              <Layers className="h-5 w-5 text-sky-600" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
                  {pkg.playbookName}
                </h3>
                {pkg.currentVersion && (
                  <Badge className="bg-sky-100 text-sky-700 text-xs flex-shrink-0">
                    v{pkg.currentVersion}
                  </Badge>
                )}
                {pkg.playbookProductName && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-sky-400 to-cyan-500 text-white shadow-sm flex-shrink-0">
                    {pkg.playbookProductName}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {pkg.playbookDescription && pkg.playbookDescription.length > 300
                  ? `${pkg.playbookDescription.substring(0, 300)}...`
                  : pkg.playbookDescription || "No description provided."}
              </p>
            </div>
          </div>
          {/* Actions at bottom */}
          <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-gray-100">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewPlaybook(pkg.playbookId)}
              className="border-2 border-sky-300 text-sky-600 hover:bg-sky-50 hover:border-sky-400 transition-all duration-200 px-3 py-2 font-semibold"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEditPlaybook(pkg.playbookId)}
              className="border-2 border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400 transition-all duration-200 px-3 py-2 font-semibold"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTriggerClick(pkg)}
              className="border-2 border-green-300 text-green-600 hover:bg-green-50 hover:border-green-400 transition-all duration-200 px-3 py-2 font-semibold"
              disabled={isDeletingId === pkg.playbookId}
            >
              <Play className="h-4 w-4 mr-1" />
              Trigger
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteClick(pkg.playbookId)}
              className="border-2 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 transition-all duration-200 px-3 py-2 font-semibold"
              disabled={isDeletingId === pkg.playbookId}
            >
              {isDeletingId === pkg.playbookId ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Calculate stats
  const stats = {
    total: playbookFilters.playbooks.length,
    active: currentPlaybooks.length,
    products: new Set(
      playbookFilters.playbooks
        .filter((p) => p.playbookProductName)
        .map((p) => p.playbookProductName)
    ).size,
  };

  return (
    <div
      className="container mx-auto p-4 sm:p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased"
      ref={containerRef}
    >
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600 via-blue-500 to-cyan-700"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/30 via-transparent to-cyan-500/30"></div>

        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-sky-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>

        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>

        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <Layers className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    Playbooks
                  </h1>
                  <p className="text-sky-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Design and execute automated workflows that orchestrate your
                    agents to complete complex tasks
                  </p>
                </div>
              </div>

              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">
                      {stats.total}
                    </span>{" "}
                    Playbooks
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-cyan-200">
                      {stats.active}
                    </span>{" "}
                    Available
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-sky-200">
                      {stats.products}
                    </span>{" "}
                    Products
                  </span>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate("/studio/playbooks/add")}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold text-base sm:text-lg w-full sm:w-auto"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-xl group-hover:bg-white/30 transition-colors">
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span>Create Playbook</span>
                </div>

                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="mb-6">
        <PlaybookFilters filters={playbookFilters} />
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* View Mode Toggle */}
        <div className="flex items-center ml-auto">
          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 transition-colors ${viewMode === "grid" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-blue-500"}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 transition-colors ${viewMode === "list" ? "bg-blue-500 text-white" : "text-gray-400 hover:text-blue-500"}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 font-medium">
          {playbookFilters.hasActiveFilters ? (
            <>Found {currentPlaybooks.length || 0} filtered results</>
          ) : (
            <>
              Showing {currentPlaybooks.length} of {currentTotal} Playbooks
            </>
          )}
        </p>
      </div>

      {/* Playbooks Grid/List */}
      <div
        className={
          viewMode === "grid"
            ? "grid gap-4 sm:gap-6 grid-cols-1 lg:grid-cols-2"
            : "space-y-3"
        }
      >
        {currentPlaybooks.map((playbook) =>
          viewMode === "grid"
            ? renderPlaybookCard(playbook, sessionId)
            : renderPlaybookListItem(playbook)
        )}
      </div>

      {playbookFilters.loading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <Spinner className="w-8 h-8 text-blue-500" />
          <p className="text-gray-600">Loading Playbooks...</p>
        </div>
      ) : (
        currentPlaybooks.length === 0 && (
          <Card className="p-12 text-center border-2 border-dashed border-gray-300">
            <div className="flex flex-col items-center gap-4">
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-4 rounded-full">
                <Layers className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 tracking-tight font-['Radley','serif']">
                  {playbookFilters.hasActiveFilters
                    ? "No playbooks match your filters"
                    : "No playbooks yet"}
                </h3>
                <p className="text-gray-600 mb-4 max-w-md">
                  {playbookFilters.hasActiveFilters
                    ? "Try adjusting your filters to find what you're looking for."
                    : "Get started by creating your first playbook to orchestrate automated workflows with your agents."}
                </p>
                {!playbookFilters.hasActiveFilters && (
                  <Button
                    onClick={() => navigate("/studio/playbooks/add")}
                    className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 rounded-xl font-semibold text-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-colors">
                        <Plus className="h-5 w-5" />
                      </div>
                      <span>Create Your First Playbook</span>
                    </div>

                    {/* Subtle glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10 blur-xl"></div>
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Playbook"
        description="This will permanently delete the playbook from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {selectedPlaybook && (
        <TriggerModal
          isOpen={triggerModalOpen}
          onClose={() => setTriggerModalOpen(false)}
          playbookId={selectedPlaybook.playbookId}
          playbookName={selectedPlaybook.playbookName}
          onSubmit={handleTriggerSubmit}
        />
      )}

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default PlaybooksList;
